import { api, unwrap } from "@lumberset/api-client";
import {
  bpsToPercent,
  date,
  money,
  percentToBps,
  QuoteStatus,
  quantity,
  typedAmount,
} from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  cn,
  Field,
  IconButton,
  Input,
  Prompt,
  SectionHeader,
  Select,
  Textarea,
  TwoColumn,
  toast,
} from "@lumberset/ui";
import { ArrowDownTray, PaperPlane, Plus, SquareTwoStack, Trash } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { type LineToMatch, MatchLineDrawer } from "../../../components/match-line";
import { catalogQuery, customersQuery, quoteQuery } from "../../../lib/queries";

export const Route = createFileRoute("/_app/quotes/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(quoteQuery(params.id)),
  component: QuoteBuilder,
});

const sectionTitles: Record<string, string> = {
  walls: "Walls",
  floors: "Floors and ceilings",
  roof: "Roof",
  beams_posts: "Beams and posts",
  sheathing: "Sheathing and subfloor",
  hardware: "Hardware",
  other: "Other",
};

const NO_CUSTOMER = "none";

function QuoteBuilder() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(quoteQuery(id));
  const { data: customers } = useQuery(customersQuery());
  const { quote, lines, projectName, publicUrl, unpriced } = data;
  const [confirmSend, setConfirmSend] = useState(false);
  // The line being matched to a Catalog Item by hand, in the drawer.
  const [matching, setMatching] = useState<LineToMatch | null>(null);
  // An empty Catalog explains every "No match" at once, so the quote says so.
  const { data: catalog } = useQuery(catalogQuery({ page: 0 }));
  const catalogEmpty = catalog !== undefined && catalog.count === 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["quotes"] });
  const fail = (error: Error) => toast.error(error.message);

  const patchQuote = useMutation({
    mutationFn: (json: Parameters<(typeof api.quotes)[":id"]["$patch"]>[0]["json"]) =>
      unwrap(api.quotes[":id"].$patch({ param: { id }, json })),
    onSuccess: refresh,
    onError: fail,
  });

  const patchLine = useMutation({
    mutationFn: (input: {
      lineId: string;
      json: { quantity?: number; unitPriceCents?: number; catalogItemId?: string | null };
    }) =>
      unwrap(
        api.quotes[":id"].lines[":lineId"].$patch({
          param: { id, lineId: input.lineId },
          json: input.json,
        }),
      ),
    onSuccess: async (result, input) => {
      await refresh();
      if (input.json.catalogItemId) {
        setMatching(null);
        toast.success(`Matched to ${result.line.sku ?? "your catalog"}`, {
          description:
            result.line.unitPriceCents > 0
              ? `Priced at ${money(result.line.unitPriceCents, quote.currency)} per ${result.line.unit}. Change it on the line if you need to.`
              : "That item has no price yet. Type one on the line.",
        });
      }
    },
    onError: fail,
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) =>
      unwrap(api.quotes[":id"].lines[":lineId"].$delete({ param: { id, lineId } })),
    onSuccess: refresh,
    onError: fail,
  });

  const addLine = useMutation({
    mutationFn: () =>
      unwrap(
        api.quotes[":id"].lines.$post({
          param: { id },
          json: {
            description: "Additional item",
            quantity: 1,
            unit: "ea",
            unitPriceCents: 0,
            taxable: true,
            section: "other",
          },
        }),
      ),
    onSuccess: refresh,
    onError: fail,
  });

  const send = useMutation({
    mutationFn: () => unwrap(api.quotes[":id"].send.$post({ param: { id } })),
    onSuccess: async () => {
      await refresh();
      toast.success("Quote sent", {
        description: "Your customer has the link. You will hear when they open it.",
      });
    },
    onError: fail,
  });

  const grouped = useMemo(() => {
    const sections = new Map<string, typeof lines>();
    for (const line of lines)
      sections.set(line.section, [...(sections.get(line.section) ?? []), line]);
    return [...sections];
  }, [lines]);

  // The API counts lines still to price, deducts included, and will not send until there are none.
  const toPrice = unpriced.summary
    ? { title: unpriced.summary, help: unpricedHelp(unpriced, catalogEmpty) }
    : null;
  const noCustomer = !quote.customerId;
  const locked = quote.status === "accepted" || quote.status === "declined";
  // What still stands between this draft and the customer, said where the Send button is.
  const beforeSending = locked || quote.sentAt ? null : toPrice || noCustomer ? true : null;

  return (
    <>
      <Crumbs trail={[{ label: "Quotes", to: "/quotes" }, { label: `#${quote.number}` }]} />

      <TwoColumn
        main={
          <>
            <Container className="divide-y p-0">
              <SectionHeader
                level="h1"
                title={`Quote #${quote.number}`}
                description={projectName}
                actions={
                  <>
                    <QuoteStatus status={quote.status} />
                    <Button size="small" variant="secondary" asChild>
                      <a href={`/api/v1/quotes/${id}/pdf`} download aria-label="Download PDF">
                        <ArrowDownTray />
                        <span className="max-sm:hidden">Download </span>PDF
                      </a>
                    </Button>
                    <Button
                      size="small"
                      disabled={locked}
                      isLoading={send.isPending}
                      onClick={() => setConfirmSend(true)}
                    >
                      <PaperPlane /> {quote.sentAt ? "Send again" : "Send to customer"}
                    </Button>
                  </>
                }
              />
              {beforeSending ? (
                <div
                  className={cn(
                    "txt-small flex flex-col gap-y-1 px-6 py-3",
                    toPrice
                      ? "bg-ui-tag-orange-bg text-ui-tag-orange-text"
                      : "bg-ui-bg-subtle text-ui-fg-subtle",
                  )}
                >
                  <span className="font-medium">
                    Before sending
                    {toPrice ? `: ${toPrice.title}` : ""}
                    {toPrice && noCustomer ? " And choose a customer." : ""}
                    {!toPrice && noCustomer ? ": choose a customer." : ""}
                  </span>
                  {toPrice ? <span className="text-pretty">{toPrice.help}</span> : null}
                  {noCustomer ? (
                    <span className="text-pretty">
                      Who the quote goes to is set under Terms
                      <span className="xl:hidden"> below</span>. They get an email with a link and
                      the PDF.
                    </span>
                  ) : null}
                </div>
              ) : null}
            </Container>

            <Container className="divide-y overflow-hidden p-0">
              <SectionHeader
                title="Lines"
                actions={
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={locked}
                    onClick={() => addLine.mutate()}
                  >
                    <Plus /> Add line
                  </Button>
                }
              />
              <div className="overflow-x-auto">
                <table className="txt-compact-small w-full min-w-[640px] text-ui-fg-subtle">
                  <thead className="txt-compact-small-plus border-b border-ui-border-base bg-ui-bg-subtle text-left">
                    <tr>
                      <th className="py-2.5 pl-6 pr-3 font-medium">Item</th>
                      <th className="w-24 px-3 py-2.5 text-right font-medium">Qty</th>
                      <th className="w-32 px-3 py-2.5 text-right font-medium">Unit price</th>
                      <th className="w-28 px-3 py-2.5 text-right font-medium">Total</th>
                      <th className="w-10 pr-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(([section, sectionLines]) => (
                      <Fragment key={section}>
                        <tr className="border-b border-ui-border-base">
                          <td
                            colSpan={5}
                            className="txt-compact-xsmall-plus px-6 py-1.5 text-ui-fg-muted"
                          >
                            {sectionTitles[section] ?? section}
                          </td>
                        </tr>
                        {sectionLines.map((line) => (
                          <tr
                            key={line.id}
                            className={cn(
                              "border-b border-ui-border-base last:border-b-0",
                              line.unpriced && "bg-ui-tag-orange-bg/40",
                            )}
                          >
                            <td className="py-2 pl-6 pr-3">
                              <div className="txt-compact-small-plus text-ui-fg-base">
                                {line.description}
                              </div>
                              <div className="txt-compact-xsmall flex flex-wrap items-center gap-x-2 gap-y-1 text-ui-fg-muted">
                                {line.sku ? (
                                  <span className="code-label">{line.sku}</span>
                                ) : (
                                  <Badge color="orange">No match</Badge>
                                )}
                                {/* Matched, but the catalog item has no price: say so, not only by colour. */}
                                {line.sku && line.unpriced ? (
                                  <Badge color="orange">No price</Badge>
                                ) : null}
                                {/* The Match by hand: pick the product from the Catalog, here, without leaving the quote. */}
                                {!locked && (!line.sku || line.unpriced) ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setMatching({
                                        id: line.id,
                                        description: line.description,
                                        quantity: line.quantity,
                                        unit: line.unit,
                                        sku: line.sku,
                                      })
                                    }
                                    className="txt-compact-xsmall-plus transition-fg cursor-pointer rounded-md border border-ui-border-base bg-ui-bg-base px-1.5 py-0.5 text-ui-fg-subtle outline-none hover:text-ui-fg-interactive focus-visible:shadow-borders-focus"
                                  >
                                    {line.sku ? "Change item" : "Match to catalog"}
                                  </button>
                                ) : null}
                                {line.groupLabel ? <span>{line.groupLabel}</span> : null}
                              </div>
                            </td>
                            {/* Wide enough for "1,250" at a phone's larger input text. */}
                            <td className="min-w-28 px-3 py-2">
                              <NumberCell
                                disabled={locked}
                                value={line.quantity}
                                suffix={line.unit}
                                negative
                                onCommit={(value) =>
                                  patchLine.mutate({ lineId: line.id, json: { quantity: value } })
                                }
                              />
                            </td>
                            <td className="min-w-32 px-3 py-2">
                              <NumberCell
                                disabled={locked}
                                value={line.unitPriceCents / 100}
                                prefix="$"
                                decimals={2}
                                onCommit={(value) =>
                                  patchLine.mutate({
                                    lineId: line.id,
                                    json: { unitPriceCents: Math.round(value * 100) },
                                  })
                                }
                              />
                            </td>
                            <td className="tabular whitespace-nowrap px-3 py-2 text-right text-ui-fg-base">
                              {money(line.totalCents, quote.currency)}
                            </td>
                            <td className="pr-4">
                              <IconButton
                                aria-label={`Remove ${line.description}`}
                                size="xsmall"
                                disabled={locked}
                                onClick={() => removeLine.mutate(line.id)}
                              >
                                <Trash />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Container>
          </>
        }
        side={
          <>
            <Container className="divide-y p-0">
              <SectionHeader title="Total" />
              <dl className="txt-compact-small flex flex-col gap-y-2 px-6 py-4 text-ui-fg-subtle">
                <Row label="Materials" value={money(quote.subtotalCents, quote.currency)} />
                {quote.discountCents > 0 ? (
                  <Row label="Discount" value={`-${money(quote.discountCents, quote.currency)}`} />
                ) : null}
                <Row label="Delivery" value={money(quote.deliveryCents, quote.currency)} />
                <Row
                  label={`Tax (${bpsToPercent(quote.taxRateBps)}%)`}
                  value={money(quote.taxCents, quote.currency)}
                />
              </dl>
              <div className="flex items-baseline justify-between px-6 py-4">
                <span className="txt-compact-small-plus text-ui-fg-base">Total</span>
                <span className="tabular text-[1.375rem] font-medium tracking-[-0.02em] text-ui-fg-base">
                  {money(quote.totalCents, quote.currency)}
                </span>
              </div>
            </Container>

            <Container className="divide-y p-0">
              <SectionHeader title="Terms" />
              <div className="flex flex-col gap-y-4 px-6 py-4">
                <Field label="Customer">
                  {(control) => (
                    <Select
                      disabled={locked}
                      value={quote.customerId ?? NO_CUSTOMER}
                      onValueChange={(value) =>
                        patchQuote.mutate({ customerId: value === NO_CUSTOMER ? null : value })
                      }
                    >
                      <Select.Trigger id={control.id}>
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value={NO_CUSTOMER}>Choose a customer</Select.Item>
                        {customers?.customers.map((customer) => (
                          <Select.Item key={customer.id} value={customer.id}>
                            {customer.name}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  )}
                </Field>

                <div className="grid grid-cols-3 gap-3">
                  <Field label="Discount">
                    {(control) => (
                      <NumberCell
                        {...control}
                        align="left"
                        disabled={locked}
                        value={quote.discountCents / 100}
                        prefix="$"
                        decimals={2}
                        onCommit={(value) =>
                          patchQuote.mutate({ discountCents: Math.round(value * 100) })
                        }
                      />
                    )}
                  </Field>
                  <Field label="Delivery">
                    {(control) => (
                      <NumberCell
                        {...control}
                        align="left"
                        disabled={locked}
                        value={quote.deliveryCents / 100}
                        prefix="$"
                        decimals={2}
                        onCommit={(value) =>
                          patchQuote.mutate({ deliveryCents: Math.round(value * 100) })
                        }
                      />
                    )}
                  </Field>
                  <Field label="Tax">
                    {(control) => (
                      <NumberCell
                        {...control}
                        align="left"
                        disabled={locked}
                        value={bpsToPercent(quote.taxRateBps)}
                        suffix="%"
                        decimals={3}
                        onCommit={(value) => patchQuote.mutate({ taxRateBps: percentToBps(value) })}
                      />
                    )}
                  </Field>
                </div>

                <Field label="Note to customer" optional>
                  {(control) => (
                    <Textarea
                      {...control}
                      disabled={locked}
                      defaultValue={quote.notesToCustomer ?? ""}
                      onBlur={(event) => {
                        const value = event.target.value.trim() || null;
                        if (value !== quote.notesToCustomer)
                          patchQuote.mutate({ notesToCustomer: value });
                      }}
                    />
                  )}
                </Field>

                <p className="txt-compact-xsmall text-ui-fg-muted">
                  Valid until {date(quote.validUntil)}.
                </p>
              </div>
            </Container>

            {quote.sentAt ? (
              <Container className="flex items-center justify-between gap-x-3">
                <div className="min-w-0">
                  <p className="txt-compact-small-plus text-ui-fg-base">Customer link</p>
                  <p className="txt-compact-xsmall truncate text-ui-fg-muted">{publicUrl}</p>
                </div>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={async () => {
                    await navigator.clipboard.writeText(publicUrl);
                    toast.success("Link copied");
                  }}
                >
                  <SquareTwoStack /> Copy
                </Button>
              </Container>
            ) : null}
          </>
        }
      />

      <MatchLineDrawer
        line={matching}
        onClose={() => setMatching(null)}
        picking={patchLine.isPending}
        onPick={(lineId, catalogItemId) => patchLine.mutate({ lineId, json: { catalogItemId } })}
      />

      <Prompt open={confirmSend} onOpenChange={setConfirmSend}>
        <Prompt.Content>
          {toPrice ? (
            // Warned before sending: the quote cannot go out with a line still to price.
            <>
              <Prompt.Header>
                <Prompt.Title>Price every line before sending</Prompt.Title>
                <Prompt.Description className="text-pretty">
                  {toPrice.title} {toPrice.help} They are highlighted on the quote.
                </Prompt.Description>
              </Prompt.Header>
              <Prompt.Footer>
                <Prompt.Cancel>Back to the quote</Prompt.Cancel>
              </Prompt.Footer>
            </>
          ) : noCustomer ? (
            // Nobody to send it to yet: the customer is chosen under Terms.
            <>
              <Prompt.Header>
                <Prompt.Title>Choose a customer first</Prompt.Title>
                <Prompt.Description className="text-pretty">
                  The quote is emailed to the customer with a link and the PDF, so it needs one.
                  Choose them under Terms, or add them under Customers first.
                </Prompt.Description>
              </Prompt.Header>
              <Prompt.Footer>
                <Prompt.Cancel>Back to the quote</Prompt.Cancel>
                <Button size="small" variant="secondary" asChild>
                  <Link to="/customers">Open customers</Link>
                </Button>
              </Prompt.Footer>
            </>
          ) : (
            <>
              <Prompt.Header>
                <Prompt.Title>Send quote #{quote.number}?</Prompt.Title>
                <Prompt.Description>
                  Your customer gets an email from your yard with a link to view and accept this
                  quote for {money(quote.totalCents, quote.currency)}.
                </Prompt.Description>
              </Prompt.Header>
              <Prompt.Footer>
                <Prompt.Cancel>Not yet</Prompt.Cancel>
                <Prompt.Action tone="primary" onClick={() => send.mutate()}>
                  Send quote
                </Prompt.Action>
              </Prompt.Footer>
            </>
          )}
        </Prompt.Content>
      </Prompt>
    </>
  );
}

/**
 * What to do about lines with no price, for a line with no match and for one
 * matched to a catalog item without a price alike. The count itself comes
 * from the API, in the words it refuses to send with.
 */
function unpricedHelp(
  { lines, deducts }: { lines: number; deducts: number },
  catalogEmpty: boolean,
) {
  const why = deducts > 0 ? "A deduct with no price credits your customer nothing. " : "";
  if (catalogEmpty) {
    return `${why}Your catalog is empty, so nothing could be matched. Type a price on ${lines === 1 ? "the line" : "each line"} now, or add your catalog once and the next quotes price themselves.`;
  }
  return `${why}Type a price on ${lines === 1 ? "the line" : "each line"}, or use Match to catalog on the line to pick the product and take its price.`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-x-4">
      <dt>{label}</dt>
      <dd className="tabular text-ui-fg-base">{value}</dd>
    </div>
  );
}

/** A number that saves when the field loses focus or Enter is pressed, and only if it changed. */
function NumberCell({
  value,
  onCommit,
  prefix,
  suffix,
  decimals = 2,
  disabled,
  align = "right",
  negative = false,
  ...control
}: {
  value: number;
  onCommit: (value: number) => void;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  disabled?: boolean;
  align?: "left" | "right";
  /** A quantity may be negative: a deduct on a Change Order, or returned material. */
  negative?: boolean;
  id?: string;
}) {
  const shown = Number.isInteger(value)
    ? String(value)
    : value.toFixed(decimals).replace(/0+$/, "").replace(/\.$/, "");
  return (
    <Input
      {...control}
      // Remount when the saved value changes, so the field shows what the server accepted.
      key={shown}
      size="small"
      inputMode="decimal"
      disabled={disabled}
      defaultValue={shown}
      prefix={prefix}
      suffix={suffix}
      className={cn("tabular", align === "right" && "text-right")}
      aria-label={quantity(value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      onBlur={(event) => {
        const next = typedAmount(event.target.value, negative);
        if (Number.isFinite(next) && next !== value) onCommit(next);
        else event.target.value = shown;
      }}
    />
  );
}
